<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>

    <div class="sl-table" :style="gridStyle">

        <!-- headers -->
        <div class="sl-table-headers">

            <!-- space for chevron -->
            <div v-if="isOpenable" class="sl-table-header"></div>

            <div v-for="column in columns" :key="column.key" class="sl-table-header" :class="getHeaderCssClass(column)">
                <template v-if="column.label">
                    {{ column.label }}
                </template>
                <template v-else-if="column.tlabel">
                    {{ $t(column.tlabel) }}
                </template>
                <span v-if="column.unit" class="text-xs text-gray-400">
                    [ {{ column.unit }} ]
                </span>
            </div>

        </div>

        <!-- rows + details -->
        <template v-for="row in getRows()" :key="row[rowUid]">

            <!-- row -->
            <div class="sl-table-row" :class="getRowCssClass(row)" @click="toggleDetails(row[rowUid])">

                <!-- chevron (displayed or not) -->
                <div v-if="isOpenable" class="sl-table-cell items-center">
                    <div v-if="row.details_component" class="sl-table-cell__chevron" />
                </div>

                <!-- cell -->
                <div v-for="column in columns" :key="column.key" class="sl-table-cell" :class="getCellCssClass(column, row)">

                    <!-- custom component -->
                    <template v-if="column.cellComponent">
                        <component :is="column.cellComponent" :row="row" :column="column" :rows="rows" :updating="updating" v-bind="$attrs" />
                    </template>

                    <!-- formatted value -->
                    <template v-else>
                        {{ getValue(row, column) }}
                    </template>

                </div>

            </div>

            <!-- details -->
            <div
                v-if="row.details_component && openRowUids.includes(row[rowUid])"
                class="sl-table-details"
                :class="openRowUids.includes(row[rowUid]) ? 'sl-table-details--open' : 'sl-table-details--closed'"
            >
                <component :is="row.details_component" ref="detailsComponent" :row="row" :updating="updating" v-bind="$attrs" />
            </div>

        </template>

    </div>

</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>

/**
 * Table properties:
 * - columns:           See after
 * - rows:              Array of objects to display
 * - rowUid:            Unique identifier of a row (default: "id")
 * - updating:          Boolean indicating that the table is currently updating it's content
 *
 * Column properties:
 * - key:               Key where to search the value from a row
 * - label:             Column's label (header)
 * - tlabel:            Column's label translation key (header)
 * - unit:              Column unit (in header)
 * - formatters:        "formatters" to apply (see Formatters.js)
 * - format:            Callback function to format the value (parameters: val, row)
 * - default:           Default value if key is not found in the row
 * - cellComponent:     Custom vue component to replace default display
 * - cssClass:          CSS Classes to apply to current column
 * - headerCssClass:    CSS Classes to apply to current column
 * - align:             Alignement: "left", "right", "center"
 * - detailsComponent:  Details component to use
 */

import { get } from 'lodash-es';

export default
{
    name: 'SlTable',

    props:
    {
        columns:
        {
            type:     Array,
            required: true,
        },
        rows:
        {
            type:     Array,
            required: true,
        },
        rowUid:
        {
            type:    String,
            default: 'id',
        },
        updating:
        {
            type:    Boolean,
            default: false,
        },
    },

    emits: [],

    data()
    {
        return {
            openRowUids: [],
        };
    },

    computed:
    {
        /**
         * If at least one row has a "details_component" key.
         */
        isOpenable()
        {
            for(const row of this.rows)
            {
                if(row.details_component)
                {
                    return true;
                }
            }

            return false;
        },

        gridStyle()
        {
            const nbColumns = this.columns.length;
            let gtc = `repeat(${nbColumns}, auto)`;

            if(this.isOpenable)
            {
                gtc = '30px ' + gtc;
            }

            return {
                gridTemplateColumns: gtc,
            };
        },
    },

    methods:
    {
        hasDetails(row)
        {
            return !!row.details_component;
        },

        toggleDetails(rowUid)
        {
            this.openRowUids = this.openRowUids.includes(rowUid) ? this.openRowUids.filter(t => t !== rowUid) : [...this.openRowUids, rowUid];
        },

        getRows()
        {
            return this.rows;
        },

        getValue(row, column)
        {
            let val = get(row, column.key, column.default);

            if(column.format)
            {
                const fn = column.format;
                val = fn(val, row);
            }
            else if(column.formatters)
            {
                val = this.$f(val, column.formatters);
            }

            return val;
        },

        getRowCssClass(row)
        {
            if(row.details_component)
            {
                if(this.openRowUids.includes(row[this.rowUid]))
                {
                    return 'sl-table-row--open';
                }
                else
                {
                    return 'sl-table-row--closed';
                }
            }
        },

        getCellCssClass(column, row)
        {
            const cssClasses = column.cssClass ? column.cssClass.split(' ') :  [];

            if(column.align)
            {
                switch(column.align)
                {
                    case 'left':
                        cssClasses.push('justify-start');
                        break;

                    case 'right':
                        cssClasses.push('justify-end');
                        break;

                    case 'center':
                        cssClasses.push('justify-center');
                        break;

                    default:
                        throw new Error(`"align" property only supports "left", "right" or "center" value.`);
                }
            }

            return cssClasses;
        },

        getHeaderCssClass(column)
        {
            const headerCssClasses = column.headerCssClass ?? [];

            if(column.align)
            {
                switch(column.align)
                {
                    case 'left':
                        headerCssClasses.push('text-start');
                        break;

                    case 'right':
                        headerCssClasses.push('text-end');
                        break;

                    case 'center':
                        headerCssClasses.push('text-center');
                        break;

                    default:
                        throw new Error(`"align" property only supports "left", "right" or "center" value.`);
                }
            }

            return headerCssClasses;
        },
    },
};

</script>

<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style scoped>
@reference "#main.css";


.sl-table
{
    display: grid;
    gap: 0;
}

.sl-table-headers, .sl-table-row
{
    display: contents;
}

.sl-table-header
{
    @apply py-2 border-b border-primary-200 text-primary-500;
}

.sl-table-row
{
    @apply hover:bg-primary-500;
}

.sl-table-cell
{
    @apply flex items-center py-0.5 border-b border-primary-200;
}

.sl-table-cell__chevron
{
    margin-top: 1px;
    margin-left: 3px;
    @apply text-xs text-primary-300;
}

.sl-table-row--open, .sl-table-row--closed
{
    @apply cursor-pointer;
}

.sl-table-row--open .sl-table-cell__chevron::after
{
    content: "\25BC";
}

.sl-table-row--closed .sl-table-cell__chevron::after
{
    content: "\25B6";
}

.sl-table-details
{
    grid-column: 1 / -1;
}

.sl-table-details--open
{
    display: grid;
}

.sl-table-details--closed
{
    display: none;
}

</style>
